import BlockContent from '@sanity/block-content-to-react';
import { GatsbyImage, StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';

import type { ISanityCopy } from '../../fragments/sanity-copy-parts';
import type { ISanityCopyWithImage } from '../../fragments/sanity-copy-with-image-parts';
import type { ISanityFigure } from '../../fragments/sanity-figure-parts';
import type { TColourScheme } from '../../types';
import { classNames } from '../../utils/classnames';
import {
  BG_COLOUR_MAP,
  PROSE_COLOUR_MAP,
  TEXT_COLOUR_MAP,
} from '../../utils/object-dictionaries';
import { ButtonLink } from '../button-link';
import { ContactInfo } from '../contact-info';
import { Difficulty } from '../module-elements/difficulty';

interface CopyWithImageProps {
  copyWithImage: ISanityCopyWithImage;
  title: string;
}

function CopyWithImage({
  copyWithImage,
  title,
}: CopyWithImageProps): JSX.Element {
  return (
    <div className={classNames(BG_COLOUR_MAP[copyWithImage.colourScheme])}>
      {(title === 'Home' || title === 'About') && (
        <div className="relative">
          <div className="absolute top-12 -right-1">
            <StaticImage
              src="../../images/assets/puzzle.png"
              alt=""
              width={150}
            />
          </div>
        </div>
      )}

      {title === 'Corporate Events' &&
      copyWithImage.colourScheme === 'secondary' ? (
        <div className="relative">
          <div className="absolute -top-32 opacity-30">
            <StaticImage
              src="../../images/assets/steps-grayscale.png"
              alt=""
              width={270}
            />
          </div>
        </div>
      ) : (
        ''
      )}

      <div
        className={classNames(
          title === 'Corporate Events' ||
            copyWithImage.modules.find(
              (e) => e._type === 'copy' && e.title === 'Notice'
            )
            ? 'py-12'
            : 'py-12 lg:py-32',
          'w-full px-4 mx-auto max-w-prose lg:max-w-screen-2xl sm:px-6 lg:px-8'
        )}
      >
        <div
          className={classNames(
            title === 'Corporate Events' ? ' ' : ' items-center',
            'grid gap-16 lg:grid-cols-2'
          )}
        >
          {copyWithImage.modules.map((module) => {
            if (module._type === 'copy') {
              return (
                <div className="" key={module.id}>
                  <Copy
                    module={module}
                    colourScheme={copyWithImage.colourScheme}
                    title={title}
                  />
                </div>
              );
            }
            if (module._type === 'figure') {
              return <Figure key={module.id} module={module} title={title} />;
            }
            if (module._type === 'contactInfo') {
              return (
                <ContactInfo
                  key={module.id}
                  title={module.title}
                  colourScheme={copyWithImage.colourScheme}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );
}

interface FigureProps {
  module: ISanityFigure;
  title: string;
}

function Figure({ module, title }: FigureProps): JSX.Element {
  // console.log(module);
  return (
    <div
      className={`relative order-last ${
        module.alt === 'moveup' ? ' xl:-mt-52 lg:-mt-80' : 'xl:-mt-0 lg:-mt-0'
      } lg:order-none`}
    >
      <div
        className="relative overflow-hidden"
        style={{ paddingBottom: `${100 / module.customRatio}%` }}
      >
        <div className="absolute inset-0 flex w-full h-full">
          <GatsbyImage
            image={module.asset.gatsbyImageData}
            alt=""
            imgClassName="flex-1 rounded-3xl"
          />
        </div>
      </div>

      <div className="">
        {title === 'Home' && (
          <div>
            <div className="absolute bottom-0 -left-24 xl:-bottom-28">
              <StaticImage
                src="../../images/assets/puzzle.png"
                alt=""
                width={150}
              />
            </div>
          </div>
        )}
        {title === 'Corporate Events' && (
          <div>
            <div className="absolute pointer-events-none -left-64 -bottom-96 top-96 opacity-30">
              <StaticImage
                src="../../images/assets/steps-grayscale.png"
                alt=""
                width={300}
              />
            </div>
          </div>
        )}
        {module.alt === 'The Alchemist' ? (
          <div className="relative">
            <div className="absolute pointer-events-none bottom-96 -right-72">
              <StaticImage
                src="../../images/assets/steps-2.png"
                alt=""
                width={500}
              />
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
}

interface CopyProps {
  module: ISanityCopy;
  colourScheme: TColourScheme;
  title: string;
}

function Copy({ module, colourScheme, title }: CopyProps): JSX.Element {
  return (
    <div className="">
      {title === 'Games' && (
        <div className="relative">
          {module.title === 'The Detective’s Office' ? (
            <div className="absolute pointer-events-none xl:-top-52 -right-56 opacity-10">
              <StaticImage
                src="../../images/assets/magnifying-glass.png"
                alt=""
                width={600}
              />
            </div>
          ) : module.title === 'Sabotage' ? (
            <div className="absolute pointer-events-none -left-56 -top-96 opacity-30">
              <StaticImage
                src="../../images/assets/steps-grayscale.png"
                alt=""
                width={270}
              />
            </div>
          ) : module.title === 'Sabotage' ? (
            <div className="absolute pointer-events-none -left-56 -top-96 opacity-30">
              <StaticImage
                src="../../images/assets/steps-grayscale.png"
                alt=""
                width={270}
              />
            </div>
          ) : (
            ''
          )}
        </div>
      )}
      <div className="">
        {module.title ? (
          <h3 className="font-serif text-3xl xl:text-5xl text-highlight-ochre">
            {module.title === 'Notice' ? null : module.title}
          </h3>
        ) : null}

        <BlockContent
          renderContainerOnSingleChild
          blocks={module._rawBody}
          className={classNames(
            'text-xs md:text-sm leading-7 my-8 tracking-wider',
            TEXT_COLOUR_MAP[colourScheme],
            PROSE_COLOUR_MAP[colourScheme]
          )}
        />
        {module.difficulty ? (
          <div className="mb-12">
            <Difficulty difficulty={module.difficulty} />
          </div>
        ) : (
          ''
        )}
        {module.ctas
          ? module.ctas.map((cta) => (
              <p key={cta.id} className="mt-5">
                <ButtonLink link={cta} colourScheme={colourScheme} />
              </p>
            ))
          : null}
      </div>
      {title === 'Home' && (
        <div className="relative">
          <div className="absolute pointer-events-none left-60 -top-6">
            <StaticImage
              src="../../images/assets/steps-1.png"
              alt=""
              width={400}
            />
          </div>
        </div>
      )}
      <div className="relative">
        {title === 'About' && (
          <div className="absolute pointer-events-none top-16 -left-32">
            <StaticImage
              src="../../images/assets/puzzle-2.png"
              alt=""
              width={150}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export { CopyWithImage };
export type { ISanityCopyWithImage };
